import { NavLink, useParams, useNavigate, useLocation } from "react-router-dom"
import { LayoutDashboard, Users, Droplets, Gauge, UserCog, Smartphone } from "lucide-react"
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  useSidebar,
} from "@/components/ui/sidebar"
import { useAuth } from "@/hooks/useAuth"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import useCurrentUser from "@/hooks/use-current-user"
import { hasReadPermission } from "@/lib/admin-permissions"

export function CouncilSidebar() {
  const { state } = useSidebar()
  const collapsed = state === "collapsed"
  const { councilId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const { councils, hasRole } = useCurrentUser()
  const currentCouncilId = councilId ? Number(councilId) : undefined
  const permissions = user?.permissions ?? []
  const canRead = (object: Parameters<typeof hasReadPermission>[1]) =>
    hasRole("super_admin") || hasReadPermission(permissions, object, currentCouncilId)

  const currentCouncil = councils?.find((c) => String(c.id) === councilId)

  const handleLogout = () => {
    logout()
    navigate("/login")
  }

  return (
    <Sidebar className={collapsed ? "w-16" : "w-64"}>
      <SidebarHeader className="border-b border-sidebar-border p-4">
        {!collapsed && (
          <>
            <div className="flex flex-col items-center justify-center gap-3 mb-4">
              <img src="/main-logo.jpg" alt="Water Bureau" className="h-24 w-32" />
              <div>
                <p className="text-xs text-muted-foreground">{currentCouncil?.name} Admin Portal</p>
              </div>
            </div>
            <div className="text-sm mb-4">
              <p className="font-medium">{currentCouncil?.name}</p>
              <div className="flex items-center gap-2 mt-2">
                <Avatar className="size-6">
                  <AvatarFallback className="text-xs">
                    {hasRole("super_admin")
                      ? "CA"
                      : user?.councils?.[0]?.name
                          ?.split(" ")
                          .map((n: string) => n[0])
                          .join("") || "AU"}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs text-muted-foreground">
                  {hasRole("super_admin") ? "Council Admin" : user?.name}
                </span>
              </div>
            </div>
          </>
        )}
      </SidebarHeader>

      <SidebarContent className="p-2">
        <SidebarGroup>
          <SidebarMenu>
            {canRead("dashboard") && (
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location.pathname === `/council/${councilId}`}>
                  <NavLink to={`/council/${councilId}`} end>
                    <LayoutDashboard className="size-4" />
                    {!collapsed && <span>Dashboard</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}

            {canRead("admin_management") && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location.pathname.includes(`/council/${councilId}/manage-admin`)}
                >
                  <NavLink to={`/council/${councilId}/manage-admin`}>
                    <UserCog className="size-4" />
                    {!collapsed && <span>Manage Admin</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}

            {canRead("devices") && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location.pathname.includes(`/council/${councilId}/devices`)}
                >
                  <NavLink to={`/council/${councilId}/devices`}>
                    <Droplets className="size-4" />
                    {!collapsed && <span>Devices</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}

            {canRead("customers") && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location.pathname.includes(`/council/${councilId}/customers`)}
                >
                  <NavLink to={`/council/${councilId}/customers`}>
                    <Users className="size-4" />
                    {!collapsed && <span>Customers</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}

            {canRead("water_efficiency") && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location.pathname.includes(`/council/${councilId}/water-efficiency`)}
                >
                  <NavLink to={`/council/${councilId}/water-efficiency`}>
                    <Gauge className="size-4" />
                    {!collapsed && <span>Water Efficiency</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}

            {canRead("app_version") && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location.pathname.includes(`/council/${councilId}/app-version`)}
                >
                  <NavLink to={`/council/${councilId}/app-version`}>
                    <Smartphone className="size-4" />
                    {!collapsed && <span>App Version</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
          </SidebarMenu>
        </SidebarGroup>

        <div className="mt-auto p-2">
          <Button variant="outline" size="sm" onClick={handleLogout} className="w-full">
            {collapsed ? "Exit" : "Sign Out"}
          </Button>
        </div>
      </SidebarContent>
    </Sidebar>
  )
}
